import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import ExportButton from "../components/ExportButton";
import { money, formatDate, todayISODate } from "../lib/format";
import { useCountry, countryInfo } from "../context/CountryContext";
import { Plus, Trash, X, Copy, ArrowUDownLeft } from "@phosphor-icons/react";

const STATUSES = ["Pending", "Sent", "Received", "Settled"];
const PAYMENT_MODES = [
  { v: "cash", label: "Cash",  hint: "Physical handover" },
  { v: "momo", label: "MoMo",  hint: "Mobile money tx" },
  { v: "bank", label: "Bank",  hint: "Bank transfer" },
  { v: "card", label: "Card",  hint: "Card payment" },
];
const DEFAULT = (country = "GH") => ({
  date: todayISODate(),
  driver_name: "", vehicle_number: "",
  amount: "", purpose: "", sent_by: "",
  status: "Sent", notes: "",
  country,
  currency: countryInfo(country).currency,
  payment_mode: "cash",
  reference_number: "",
});

// Map a transfer record into form shape, stripping per-instance fields
const fromTransfer = (t) => ({
  date: todayISODate(),                  // always today
  driver_name: t.driver_name || "",
  vehicle_number: t.vehicle_number || "",
  amount: t.amount != null ? String(t.amount) : "",
  purpose: t.purpose || "",
  sent_by: t.sent_by || "",
  status: "Sent",                        // start fresh
  notes: "",
  country: t.country || "GH",
  currency: t.currency || countryInfo(t.country || "GH").currency,
  payment_mode: t.payment_mode || "cash",
  reference_number: "",                   // fresh tx ref for the new transfer
});

const STATUS_STYLES = {
  Pending:  "bg-amber-50 text-amber-700 border-amber-200",
  Sent:     "bg-orange-50 text-ps-orange border-orange-200",
  Received: "bg-sky-50 text-ps-blue border-sky-200",
  Settled:  "bg-lime-50 text-lime-700 border-lime-200",
};

export default function MoneyOnRoad() {
  const { scope, filterParam, defaultCountry, isAll } = useCountry();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(() => DEFAULT(defaultCountry));
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [copiedFrom, setCopiedFrom] = useState("");

  const fillFromTransfer = (t) => {
    setForm(fromTransfer(t));
    setCopiedFrom(`${t.driver_name || "—"} · ${money(t.amount, t.currency)}`);
  };
  const copyLast = () => { if (rows.length) fillFromTransfer(rows[0]); };
  const duplicateRow = (e, t) => { e.stopPropagation(); fillFromTransfer(t); setOpen(true); };
  const clearCopy = () => { setForm(DEFAULT(defaultCountry)); setCopiedFrom(""); };

  const load = async () => {
    setLoading(true);
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (filterParam) params.country = filterParam;
    const [m, dr, tr] = await Promise.all([
      api.get("/transfers", { params }),
      api.get("/drivers"),
      api.get("/trucks"),
    ]);
    setRows(m.data);
    setDrivers(dr.data);
    setTrucks(tr.data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, filterParam]);

  // When the scope changes, sync the new-record default country (but only if not currently copying)
  useEffect(() => {
    if (!open && !copiedFrom) {
      setForm((f) => ({ ...f, country: defaultCountry, currency: countryInfo(defaultCountry).currency }));
    }
    // eslint-disable-next-line
  }, [defaultCountry]);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/transfers", {
        ...form,
        date: new Date(form.date).toISOString(),
        amount: parseFloat(form.amount),
      });
      setOpen(false);
      setForm(DEFAULT(defaultCountry));
      setCopiedFrom("");
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const cycleStatus = async (id, current) => {
    const idx = STATUSES.indexOf(current);
    const next = STATUSES[(idx + 1) % STATUSES.length];
    await api.patch(`/transfers/${id}`, { status: next });
    await load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this transfer?")) return;
    await api.delete(`/transfers/${id}`);
    await load();
  };

  // Sum per currency so GHS + XOF totals don't collide
  const totalsByCur = rows.reduce((acc, r) => {
    const c = r.currency || "GHS";
    if (!acc[c]) acc[c] = { sent: 0, settled: 0 };
    acc[c].sent += r.amount;
    if (r.status === "Settled") acc[c].settled += r.amount;
    return acc;
  }, {});
  const currencyKeys = Object.keys(totalsByCur);

  return (
    <div data-testid="money-page">
      <PageHeader
        subtitle={isAll ? "Driver cash flow · All countries" : `Driver cash flow · ${countryInfo(scope).flag} ${countryInfo(scope).name}`}
        title="Money on Road"
        action={
          <div className="flex gap-2">
            <ExportButton
              endpoint="road-expenses/export"
              filenameBase={`road-expenses-${new Date().toISOString().slice(0, 7)}`}
              params={{ country: filterParam || undefined }}
              testidPrefix="money-export"
            />
            {rows.length > 0 && (
              <button
                onClick={() => { copyLast(); setOpen(true); }}
                data-testid="copy-last-transfer-button"
                className="border border-slate-300 bg-white text-ps-deep px-3 py-2.5 rounded-md font-semibold flex items-center gap-2 hover:bg-slate-50 transition-colors text-sm"
                title={`Copy from ${rows[0].driver_name}`}
              >
                <Copy size={16} weight="bold" /> Copy from last
              </button>
            )}
            <button onClick={() => setOpen(true)} data-testid="new-transfer-button" className="bg-ps-blue text-white px-4 py-2.5 rounded-md font-semibold flex items-center gap-2 hover:bg-ps-deep transition-colors shadow-sm">
              <Plus size={16} weight="bold" /> Send money
            </button>
          </div>
        }
      />

      <div className="px-8 py-4 bg-slate-100 border-b border-slate-200 flex gap-8 items-end flex-wrap">
        <div>
          <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Status</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="filter-status" className="border border-slate-300 bg-white px-2 py-2 text-sm font-mono-data">
            <option value="">All</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="ml-auto flex gap-6 flex-wrap" data-testid="totals-by-currency">
          {currencyKeys.length === 0 && (
            <div><div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Total sent</div><div className="font-mono-data tabular text-xl">—</div></div>
          )}
          {currencyKeys.map((cur) => {
            const t = totalsByCur[cur];
            return (
              <div key={cur} className="border-l border-slate-200 pl-6 first:border-l-0 first:pl-0" data-testid={`totals-${cur}`}>
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 flex items-center gap-1">
                  <span className="px-1.5 py-0.5 rounded-sm bg-slate-200 text-slate-700 font-bold text-[9px]">{cur}</span>
                </div>
                <div className="flex gap-5 mt-0.5">
                  <div><div className="text-[10px] text-slate-500">Sent</div><div className="font-mono-data tabular text-lg">{money(t.sent, cur)}</div></div>
                  <div><div className="text-[10px] text-slate-500">Settled</div><div className="font-mono-data tabular text-lg text-lime-700">{money(t.settled, cur)}</div></div>
                  <div><div className="text-[10px] text-slate-500">Outstanding</div><div className="font-mono-data tabular text-lg text-amber-700">{money(t.sent - t.settled, cur)}</div></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="p-8">
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-600 border-b border-slate-200">
              <tr>
                <th className="px-3 py-3 text-left">Date</th>
                {isAll && <th className="px-3 py-3 text-left w-12"></th>}
                <th className="px-3 py-3 text-left">Driver</th>
                <th className="px-3 py-3 text-left">Truck</th>
                <th className="px-3 py-3 text-left">Purpose</th>
                <th className="px-3 py-3 text-right">Amount</th>
                <th className="px-3 py-3 text-left">Sent by</th>
                <th className="px-3 py-3 text-left">Status</th>
                <th className="px-3 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-testid="transfers-table-body">
              {loading && <tr><td colSpan={isAll ? 9 : 8} className="p-6"><div className="indus-bar w-1/3" /></td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={isAll ? 9 : 8} className="p-8 text-center text-slate-500">No transfers yet.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono-data text-xs">{formatDate(r.date)}</td>
                  {isAll && <td className="px-3 py-2 text-sm" title={countryInfo(r.country).name}>{countryInfo(r.country).flag}</td>}
                  <td className="px-3 py-2 font-semibold">{r.driver_name}</td>
                  <td className="px-3 py-2 font-mono-data">{r.vehicle_number || "—"}</td>
                  <td className="px-3 py-2">
                    <div>{r.purpose}</div>
                    {(r.payment_mode && r.payment_mode !== "cash") || r.reference_number ? (
                      <div className="text-[10px] text-slate-500 font-mono-data mt-0.5 flex items-center gap-1 flex-wrap" data-testid={`transfer-${r.id}-payment-meta`}>
                        <span className="bg-slate-100 px-1.5 py-0.5 rounded uppercase tracking-wider font-semibold">{(r.payment_mode || "cash").toUpperCase()}</span>
                        {r.reference_number && <span title="Reference">· {r.reference_number}</span>}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-right font-mono-data tabular text-lime-700 font-semibold">{money(r.amount, r.currency)}</td>
                  <td className="px-3 py-2 text-slate-600">{r.sent_by}</td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => cycleStatus(r.id, r.status)}
                      data-testid={`status-toggle-${r.id}`}
                      className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${STATUS_STYLES[r.status]} hover:opacity-80`}
                      title="Click to cycle status"
                    >
                      {r.status}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => duplicateRow(e, r)}
                        data-testid={`duplicate-transfer-${r.id}`}
                        title="Duplicate this transfer"
                        className="text-slate-400 hover:text-ps-blue p-1 rounded hover:bg-sky-50 transition-colors"
                      >
                        <Copy size={14} weight="bold" />
                      </button>
                      <button onClick={() => del(r.id)} data-testid={`delete-transfer-${r.id}`} className="text-slate-400 hover:text-red-600 p-1"><Trash size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex justify-end" onClick={() => { setOpen(false); setCopiedFrom(""); }} data-testid="transfer-dialog">
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white h-full overflow-y-auto border-l border-slate-200">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Cash transfer</div>
                <h2 className="font-heading font-black text-2xl tracking-tight">Send money</h2>
              </div>
              <button type="button" onClick={() => { setOpen(false); setCopiedFrom(""); }} className="text-slate-500"><X size={20} /></button>
            </div>

            {/* Copy-from banner / shortcut */}
            <div className="px-6 pt-4">
              {copiedFrom ? (
                <div className="flex items-center justify-between bg-sky-50 border border-sky-200 rounded-md px-3 py-2 text-sm" data-testid="transfer-copied-from-banner">
                  <div className="flex items-center gap-2 text-ps-deep">
                    <Copy size={14} weight="fill" className="text-ps-blue" />
                    <span>Copied from <span className="font-mono-data font-semibold">{copiedFrom}</span> &mdash; review &amp; edit.</span>
                  </div>
                  <button
                    type="button"
                    onClick={clearCopy}
                    data-testid="transfer-clear-copy"
                    className="text-xs uppercase tracking-wider font-bold text-ps-blue hover:text-ps-deep flex items-center gap-1"
                  >
                    <ArrowUDownLeft size={12} weight="bold" /> Clear
                  </button>
                </div>
              ) : rows.length > 0 ? (
                <button
                  type="button"
                  onClick={copyLast}
                  data-testid="transfer-dialog-copy-last"
                  className="w-full border border-dashed border-slate-300 rounded-md px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:border-ps-blue hover:text-ps-deep transition-colors flex items-center justify-center gap-2"
                >
                  <Copy size={14} weight="bold" />
                  Copy from last transfer — <span className="font-mono-data font-semibold">{rows[0].driver_name}</span>
                  <span className="text-xs text-slate-500">({money(rows[0].amount, rows[0].currency)})</span>
                </button>
              ) : null}
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Country *</label>
                <div className="grid grid-cols-2 gap-2" data-testid="transfer-country-toggle">
                  {["GH", "TG"].map((c) => {
                    const info = countryInfo(c);
                    const active = form.country === c;
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, country: c, currency: info.currency }))}
                        data-testid={`transfer-country-${c}`}
                        className={`border rounded-md px-3 py-2 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
                          active ? "border-ps-blue bg-sky-50 text-ps-deep" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        <span className="text-base leading-none">{info.flag}</span>
                        <span>{info.name}</span>
                        <span className="text-[10px] font-mono-data text-slate-500 ml-auto">{info.currency}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <FormField label="Date *" type="date" value={form.date} onChange={onChange("date")} testid="transfer-date" required />
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Driver *</label>
                <input list="driver-list" value={form.driver_name} onChange={onChange("driver_name")} required data-testid="transfer-driver" className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 font-mono-data" />
                <datalist id="driver-list">{drivers.map((d) => <option key={d.id} value={d.name} />)}</datalist>
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Truck</label>
                <input list="truck-list" value={form.vehicle_number} onChange={onChange("vehicle_number")} data-testid="transfer-truck" className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 font-mono-data" />
                <datalist id="truck-list">{trucks.map((t) => <option key={t.id} value={t.vehicle_number} />)}</datalist>
              </div>
              <FormField label={`Amount (${form.currency || "GHS"}) *`} type="number" step="0.01" value={form.amount} onChange={onChange("amount")} testid="transfer-amount" required />
              <FormField label="Purpose *" value={form.purpose} onChange={onChange("purpose")} testid="transfer-purpose" placeholder="Road toll, food, repair…" required />
              <FormField label="Sent by *" value={form.sent_by} onChange={onChange("sent_by")} testid="transfer-sent-by" required />
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">Payment mode</label>
                <div className="grid grid-cols-4 gap-2" data-testid="transfer-payment-mode-toggle">
                  {PAYMENT_MODES.map((o) => {
                    const active = (form.payment_mode || "cash") === o.v;
                    return (
                      <button
                        key={o.v}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, payment_mode: o.v }))}
                        data-testid={`transfer-payment-mode-${o.v}`}
                        className={`border rounded-md px-2 py-1.5 text-xs font-semibold transition-colors ${active ? "border-ps-blue bg-sky-50 text-ps-deep" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}`}
                        title={o.hint}
                      >
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {form.payment_mode !== "cash" && (
                <FormField
                  label={form.payment_mode === "momo" ? "MoMo transaction ref" : form.payment_mode === "bank" ? "Bank reference / receipt #" : "Card auth / receipt #"}
                  value={form.reference_number}
                  onChange={onChange("reference_number")}
                  testid="transfer-reference-number"
                  placeholder={form.payment_mode === "momo" ? "e.g. MP240321.1052.A12345" : ""}
                />
              )}
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Status</label>
                <select value={form.status} onChange={onChange("status")} data-testid="transfer-status" className="w-full border border-slate-300 px-3 py-2 text-sm font-mono-data">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <FormField label="Notes" value={form.notes} onChange={onChange("notes")} testid="transfer-notes" />
              {err ? <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2">{err}</div> : null}
            </div>
            <div className="p-6 border-t border-slate-200 flex gap-3">
              <button type="button" onClick={() => { setOpen(false); setCopiedFrom(""); }} className="flex-1 border border-slate-300 py-2.5 text-sm hover:bg-slate-100">Cancel</button>
              <button type="submit" data-testid="transfer-submit" className="flex-1 bg-ps-blue text-white py-2.5 rounded-md text-sm font-semibold hover:bg-ps-deep transition-colors">Save transfer</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function FormField({ label, testid, ...props }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">{label}</label>
      <input {...props} data-testid={testid} className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 font-mono-data" />
    </div>
  );
}
